import { Box, Paper, Stack, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { FC, useMemo } from 'react';

import type { CustomFieldDefinition, CustomFieldValueWithDefinition } from '@vy/custom-fields-types';
import type { ContractIntegrationEntity } from '@vy/types';
import { isNotEmptyArray } from '@vy/utils';

import { ColorDot } from '../../../../../../components/design/ColorDot';
import { VyTable } from '../../../../../../components/new-design/table/vy-table/VyTable';
import { VyTableCell } from '../../../../../../components/new-design/table/vy-table/VyTableCell';
import { buildCustomFieldRows } from '../../../../../../components/custom-fields/build-custom-field-rows';
import { formatDate } from '../../../../../../utils/date';
import { ContractCustomFieldEditableCell } from './ContractCustomFieldEditableCell';

type ContractCustomFieldsSectionProps = {
  contractId: string;
  customFieldsWithDefinitions: CustomFieldValueWithDefinition[] | undefined;
  customFieldDefinitions: CustomFieldDefinition[] | undefined;
  integrationEntities: ContractIntegrationEntity[];
};

const getCustomFieldRowId = (row: CustomFieldValueWithDefinition): string =>
  row.customFieldDefinitionId;

export const ContractCustomFieldsSection: FC<ContractCustomFieldsSectionProps> = ({
  contractId,
  customFieldsWithDefinitions,
  customFieldDefinitions,
  integrationEntities,
}) => {
  const rows = useMemo(
    () => buildCustomFieldRows(customFieldsWithDefinitions, customFieldDefinitions),
    [customFieldsWithDefinitions, customFieldDefinitions],
  );

  const columns = useMemo<GridColDef<CustomFieldValueWithDefinition>[]>(
    () => [
      {
        field: 'source',
        headerName: 'Source',
        width: 160,
        sortable: false,
        renderCell: (params) => (
          <VyTableCell value={params.row.definition?.integrationSource ?? 'Vayu'} containerClassName="justify-start" />
        ),
      },
      {
        field: 'fieldPath',
        headerName: 'Source field',
        width: 240,
        sortable: false,
        renderCell: (params) => (
          <VyTableCell value={params.row.definition?.fieldPath} containerClassName="justify-start" />
        ),
      },
      {
        field: 'fieldName',
        headerName: 'Vayu field',
        width: 240,
        sortable: false,
        renderCell: (params) => (
          <VyTableCell value={params.row.fieldName} containerClassName="justify-start" />
        ),
      },
      {
        field: 'value',
        headerName: 'Value',
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <ContractCustomFieldEditableCell row={params.row} contractId={contractId} />
        ),
      },
    ],
    [contractId],
  );

  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      {isNotEmptyArray(integrationEntities) && (
        <Stack spacing={2}>
          {integrationEntities.map((integration, index) => (
            <Paper
              key={index}
              variant="outlined"
              sx={{ borderRadius: '10px', overflow: 'hidden' }}
            >
              <Stack
                direction="row"
                sx={{
                  alignItems: "center",
                  gap: 1,
                  p: '12px 16px',
                  borderBottom: '1px solid rgba(13,27,39,0.1)'
                }}>
                <ColorDot sizeOverride="9px" color="#65CF7E" />
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: "14px"
                  }}>
                  {integration.integrationProvider}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                sx={{
                  gap: 3,
                  p: '12px 16px'
                }}>
                <Box>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      lineHeight: "20px",
                      mb: 1,
                      fontWeight: 400,
                      color: "text.secondary"
                    }}>
                    Import Date
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      lineHeight: "20px"
                    }}>
                    {formatDate(integration.updatedAt)}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      lineHeight: "20px",
                      mb: 1,
                      fontWeight: 400,
                      color: "text.secondary"
                    }}>
                    Sync status
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      lineHeight: "20px"
                    }}>
                    {integration.syncStatus}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
      <Paper
        elevation={1}
        sx={{
          p: '24px 32px',
          borderRadius: '10px',
          boxShadow: '0px 10px 24px rgba(0,0,0,0.03)',
          border: '1px solid rgba(13,27,39,0.1)',
          minWidth: '880px',
        }}
      >
        <Typography
          sx={{
            fontSize: 24,
            color: '#041E78',
            mb: 1
          }}>
          Custom fields
        </Typography>
        <Box>
          <VyTable
            tableName="contract-custom-fields"
            items={rows}
            itemsCount={rows.length}
            columns={columns}
            showPagination={false}
            getRowId={getCustomFieldRowId}
          />
        </Box>
      </Paper>
    </Stack>
  );
};
